'use client';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTheme } from 'next-themes';
import { Fragment, useEffect, useState } from 'react';

import { fetchAllCategories } from '@/api';
import Container from '@/components/container';
import { FacebookIcon } from '@/components/icons/FacebookIcon';
import { InstagramIcon } from '@/components/icons/InstagramIcon';
import SocialIcon from '@/components/icons/SocialIcon';
import { TwitterIcon } from '@/components/icons/TwitterIcon';
import { YouTubeIcon } from '@/components/icons/YouTubeIcon';
import ShareButton from '@/components/shared/shareButton';
import ThemeSwitch from '@/components/shared/themeswitch';
import getImage from '@/utils/getImageData';

// Menu structure definition - simplified
const MENU_STRUCTURE = {
  main: [
    { label: 'Home', href: '/' },
    { label: 'Featured', href: '/featured' },
    {
      label: 'Resources',
      type: 'submenu',
      items: [
        { label: 'Our Story', href: '/about' },
        { label: 'Contact', href: '/contact' },
        { label: 'Help Center', href: '/help' },
        { label: 'Our Team', href: '/team' },
        { label: 'Careers', href: '/careers' },
        { label: 'Guides', href: '/guides' },
      ],
    },
  ],
};

export default function Navbar(props) {
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    fetchAllCategories().then(res => setCategories(res || []));
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper function to determine if a link is active
  const isActiveLink = href => {
    if (href === '/' && pathname === '/') return true;
    if (href !== '/' && pathname.startsWith(href)) return true;
    return false;
  };

  // Combine all menu items for mobile
  const allMenuItems = [...MENU_STRUCTURE.main];

  const imageProps = props.logo ? getImage(props.logo, { width: 150 }) : null;
  const imagePropsAlt = props.logoalt ? getImage(props.logoalt, { width: 150 }) : null;

  const currentLogoProps = mounted && resolvedTheme === 'dark' ? imagePropsAlt : imageProps;

  return (
    <div
      className={`relative top-0 z-50 w-full transition-all duration-300 md:sticky ${
        scrolled
          ? 'bg-white/95 shadow-sm backdrop-blur-md dark:bg-gray-900/95'
          : 'bg-white dark:bg-gray-900'
      }`}
    >
      <Container>
        <nav className="flex items-center justify-between py-3">
          <div className="flex items-center">
            <Link
              prefetch={false}
              href="/"
              title="AllBlogs Home"
              className="group mr-6 flex items-center transition-transform duration-300 hover:scale-105"
              style={{ height: '40px' }}
            >
              {currentLogoProps && (
                <Image
                  unoptimized
                  src={currentLogoProps.src}
                  alt={`AllBlogs Logo ${resolvedTheme === 'dark' ? 'Dark' : 'Light'}`}
                  sizes="150px"
                  priority={true}
                  width={100}
                  height={40}
                  className="h-10 w-auto"
                  title="AllBlogs Logo"
                />
              )}
            </Link>

            {/* Desktop Navigation - Main Menu */}
            <div className="hidden lg:flex lg:items-center lg:space-x-3">
              {MENU_STRUCTURE.main.map((item, index) => {
                // Regular menu item
                if (!item.type) {
                  return (
                    <Link
                      prefetch={false}
                      title={item.label}
                      href={item.href}
                      key={index}
                      className={`relative rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                        isActiveLink(item.href)
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                }

                // Dropdown menu
                if (item.type === 'submenu') {
                  return (
                    <Menu as="div" className="relative" key={index}>
                      {({ open }) => (
                        <>
                          <MenuButton
                            className={`flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                              open
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400'
                            }`}
                          >
                            {item.label}
                            <ChevronDownIcon
                              className={`ml-1 h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                            />
                          </MenuButton>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-150"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <MenuItems className="ring-opacity-5 absolute left-0 z-20 mt-2 w-56 origin-top-left rounded-md bg-white py-1 shadow-xl ring-1 ring-black focus:outline-none dark:bg-gray-800 dark:ring-gray-700">
                              {item.items.map((subItem, subIndex) => (
                                <MenuItem key={subIndex}>
                                  {({ active }) => (
                                    <Link
                                      prefetch={false}
                                      href={subItem.href}
                                      title={subItem.label}
                                      className={`block px-4 py-2 text-sm ${
                                        active || isActiveLink(subItem.href)
                                          ? 'bg-gray-50 text-blue-600 dark:bg-gray-700 dark:text-blue-400'
                                          : 'text-gray-700 dark:text-gray-200'
                                      }`}
                                    >
                                      {subItem.label}
                                    </Link>
                                  )}
                                </MenuItem>
                              ))}
                            </MenuItems>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  );
                }

                return null;
              })}

              {/* Categories Dropdown */}
              <Menu as="div" className="relative">
                {({ open }) => (
                  <>
                    <MenuButton
                      className={`flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                        open || pathname.startsWith('/category')
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400'
                      }`}
                    >
                      Categories
                      <ChevronDownIcon
                        className={`ml-1 h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                      />
                    </MenuButton>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-150"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="ring-opacity-5 absolute left-0 z-20 mt-2 max-h-96 w-64 origin-top-left overflow-auto rounded-md bg-white py-1 shadow-xl ring-1 ring-black focus:outline-none dark:bg-gray-800 dark:ring-gray-700">
                        {categories.map(category => (
                          <MenuItem key={category._id}>
                            {({ active }) => (
                              <Link
                                prefetch={false}
                                href={`/category/${category?.slug?.current}`}
                                title={category.title}
                                className={`flex items-center justify-between px-4 py-2 text-sm ${
                                  active
                                    ? 'bg-gray-50 text-blue-600 dark:bg-gray-700 dark:text-blue-400'
                                    : 'text-gray-700 dark:text-gray-200'
                                }`}
                              >
                                <span>{category.title}</span>
                                <span className="ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                  {category.totalPostCount || 0}
                                </span>
                              </Link>
                            )}
                          </MenuItem>
                        ))}
                      </MenuItems>
                    </Transition>
                  </>
                )}
              </Menu>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            {/* Social Icons */}
            <div className="hidden md:flex md:items-center md:space-x-3">
              <SocialIcon
                href="https://www.youtube.com/@allblogss"
                title="AllBlogs YouTube"
                color="#FF0000"
              >
                <YouTubeIcon />
              </SocialIcon>
              <SocialIcon href="https://x.com/allblogslive" title="AllBlogs X" color="#1DA1F2">
                <TwitterIcon />
              </SocialIcon>
              <SocialIcon
                href="https://www.instagram.com/allblogs.in"
                title="AllBlogs Instagram"
                color="#E1306C"
              >
                <InstagramIcon />
              </SocialIcon>
              <SocialIcon
                href="https://www.facebook.com/people/Allblogs/100089614301458"
                title="AllBlogs Facebook"
                color="#4267B2"
              >
                <FacebookIcon />
              </SocialIcon>
              <ShareButton />
            </div>

            {/* Desktop Navigation - Secondary Menu */}
            <div className="hidden lg:flex lg:items-center lg:space-x-3">
              {/* Theme switch */}
              <ThemeSwitch />
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <Disclosure>
                {({ open }) => (
                  <div>
                    <DisclosureButton
                      aria-label="Toggle Menu"
                      className="inline-flex items-center justify-center rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    >
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                      )}
                    </DisclosureButton>

                    {/* Mobile Menu Panel */}
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-150"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <DisclosurePanel className="absolute inset-x-0 top-full mt-0 w-full origin-top transform bg-white shadow-xl dark:bg-gray-900">
                        <div className="flex flex-col space-y-1 px-4 pt-4 pb-6">
                          {/* Main menu items in mobile */}
                          {allMenuItems.map((item, index) => {
                            // Regular menu item
                            if (!item.type) {
                              return (
                                <DisclosureButton
                                  key={index}
                                  as={Link}
                                  prefetch={false}
                                  title={item.label}
                                  href={item.href}
                                  className={`block rounded-md px-3 py-2.5 text-base font-medium ${
                                    isActiveLink(item.href)
                                      ? 'text-blue-600 dark:text-blue-400'
                                      : 'text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400'
                                  }`}
                                >
                                  {item.label}
                                </DisclosureButton>
                              );
                            }

                            // Submenu in mobile
                            if (item.type === 'submenu') {
                              return (
                                <Disclosure key={index}>
                                  {({ open: subOpen }) => (
                                    <div>
                                      <DisclosureButton className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-base font-medium text-gray-700 transition-colors dark:text-gray-200">
                                        <span>{item.label}</span>
                                        <ChevronDownIcon
                                          className={`h-5 w-5 transform transition-transform duration-200 ${subOpen ? 'rotate-180' : ''}`}
                                        />
                                      </DisclosureButton>
                                      <DisclosurePanel className="py-1 pl-6">
                                        <div className="flex flex-col space-y-1">
                                          {item.items.map((subItem, subIndex) => (
                                            <DisclosureButton
                                              key={subIndex}
                                              as={Link}
                                              prefetch={false}
                                              href={subItem.href}
                                              title={subItem.label}
                                              className={`block rounded-md px-3 py-2 text-sm ${
                                                isActiveLink(subItem.href)
                                                  ? 'text-blue-600 dark:text-blue-400'
                                                  : 'text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400'
                                              }`}
                                            >
                                              {subItem.label}
                                            </DisclosureButton>
                                          ))}
                                        </div>
                                      </DisclosurePanel>
                                    </div>
                                  )}
                                </Disclosure>
                              );
                            }

                            return null;
                          })}

                          {/* Categories in Mobile Menu */}
                          <Disclosure>
                            {({ open: catOpen }) => (
                              <div>
                                <DisclosureButton className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-base font-medium text-gray-700 transition-colors dark:text-gray-200">
                                  <span>Categories</span>
                                  <ChevronDownIcon
                                    className={`h-5 w-5 transform transition-transform duration-200 ${catOpen ? 'rotate-180' : ''}`}
                                  />
                                </DisclosureButton>
                                <DisclosurePanel className="py-1 pl-6">
                                  <div className="flex max-h-60 flex-col space-y-1 overflow-y-auto">
                                    {categories.map(category => (
                                      <DisclosureButton
                                        key={category._id}
                                        as={Link}
                                        prefetch={false}
                                        href={`/category/${category?.slug?.current}`}
                                        title={category.title}
                                        className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
                                      >
                                        <span>{category.title}</span>
                                        <span className="ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                          {category.totalPostCount || 0}
                                        </span>
                                      </DisclosureButton>
                                    ))}
                                  </div>
                                </DisclosurePanel>
                              </div>
                            )}
                          </Disclosure>

                          {/* Mobile Theme Switch */}
                          <div className="mt-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                            <ThemeSwitch className="ml-3" />
                          </div>

                          {/* Social Icons in Mobile Menu */}
                          <div className="mt-3 flex items-center justify-start space-x-4 px-3 pt-3">
                            <SocialIcon
                              href="https://www.youtube.com/@allblogss"
                              title="AllBlogs YouTube"
                              color="#FF0000"
                            >
                              <YouTubeIcon />
                            </SocialIcon>
                            <SocialIcon
                              href="https://x.com/allblogslive"
                              title="AllBlogs X"
                              color="#1DA1F2"
                            >
                              <TwitterIcon />
                            </SocialIcon>
                            <SocialIcon
                              href="https://www.instagram.com/allblogs.in"
                              title="AllBlogs Instagram"
                              color="#E1306C"
                            >
                              <InstagramIcon />
                            </SocialIcon>
                            <SocialIcon
                              href="https://www.facebook.com/people/Allblogs/100089614301458"
                              title="AllBlogs Facebook"
                              color="#4267B2"
                            >
                              <FacebookIcon />
                            </SocialIcon>
                            <ShareButton />
                          </div>
                        </div>
                      </DisclosurePanel>
                    </Transition>
                  </div>
                )}
              </Disclosure>
            </div>
          </div>
        </nav>
      </Container>
    </div>
  );
}
